import React, { useState, useEffect } from 'react';
import GetUrls from 'ts/enums/Url'
import { http } from 'utils'
import toast from 'react-hot-toast';
import { Button, Input, TextArea } from 'components/ui'
import ReceiptPreview from 'components/ReceiptPreview';
import { SpinnerIcon } from 'components/svg'
import { useTrader } from 'context/TraderTypeContext';
import useAuthStore from 'store/auth'

interface StoreData {
  welcome: string;
  customerService: string;
  paidInFull: string;
  thankYou: string;
  imgUrl: string | null;
  gst: string;
}

interface UploadResponse {
  title: string;
  message: string;
  url: string;
}

const ReceiptLayoutPage = () => {
  const Url = GetUrls();
  const user = useAuthStore((state) => state.user);
  const { traderId } = useTrader();
  const [isLoading, setLoading] = useState(false);
  const [errorFile, setErrorFile] = useState<string | null>(null);
  const [data, setData] = useState<StoreData | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fetchStoreData = async (): Promise<StoreData> => {
    try {
        const response = await http.get<StoreData>(Url.getReceiptFormat(traderId))
        return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  };

  useEffect(() => {
    setImagePreview(null);  
    setSelectedFile(null);
    const loadData = async () => {
      setLoading(true);
      try {
        const storeData = await fetchStoreData();
        setData(storeData);
      } catch (error) {
        setErrorFile('Failed to fetch store data.');
      } finally {
        setLoading(false);
      }
    };
    if (traderId || user?.traders.length === 0) {
      loadData();
    }
  }, [traderId]); 
  
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newFile = e?.target?.files?.[0] || null;
    if (newFile) {
      if (newFile.size > 200 * 1024) {
        setErrorFile('File size must be under 200 KB');
        setSelectedFile(null);
        setImagePreview(null);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(newFile);
      
      setSelectedFile(newFile);
      setErrorFile(null);
    }
  };

  const uploadLogo = async (file: File): Promise<string> => {
    const currentTraderId = user.traders.length !== 0 ? traderId : user.traderId;
    const programId = user.programId;
    
    const formData = new FormData();
    formData.append('logo', file);
    
    try {
      console.log('Uploading to:', `/upload/logo/${programId}/${currentTraderId}`, formData);
      
      const response = await http.post<UploadResponse>(
        `${process.env.REACT_APP_API_HOST}/upload/logo/${programId}/${currentTraderId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.status === 200 && response.data?.url) {
        return response.data.url;
      } else {
        throw new Error('Upload failed - no image URL returned');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (data) {
      setData({
        ...data,
        [name]: value,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (data) {
      setData({
        ...data,
        [name]: value,
      });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let finalImgUrl = data?.imgUrl;
      
      if (selectedFile) {
        try {
          const uploadedImageUrl = await uploadLogo(selectedFile);
          finalImgUrl = uploadedImageUrl;
          
          setData(prevData => ({
            ...prevData!,
            imgUrl: uploadedImageUrl
          }));
          
          setSelectedFile(null);
          setImagePreview(null);
          
          toast.success('Logo uploaded successfully');
        } catch (uploadError) {
          toast.error(`Failed to upload logo: ${(uploadError as any)?.message || 'Unknown error'}`);
          throw uploadError; 
        }
      }

      const mainresp = await http.request({
        url: Url.updateReceiptFormat,
        method: 'PUT',
        data: {
          "programId": Number(user.programId),
          "traderId": Number(user.traders.length !== 0 ? traderId : user.traderId),
          "welcome": data?.welcome,
          "customerService": data?.customerService,
          "gst": data?.gst,
          "paidInFull": data?.paidInFull,
          "thankYou": data?.thankYou,
          "imgUrl": finalImgUrl,
        }
      });
      
      if (mainresp.status !== 200) {
        toast.error('Failed to update receipt layout');
        throw new Error('Failed to update receipt layout');
      }
      
      toast.success('Successfully saved');
    } catch (error) {
      toast.error(`Failed to save. ${(error as any)?.message || 'An unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayImage = () => {
    if (imagePreview) {
      return imagePreview;
    }
    return data?.imgUrl || null;
  };
  
  return (
    <div className="px-8 py-6 rounded bg-gray-50 shadow-xl mt-4 mx-4 fixed-height overflow-scroll">
      {
      <div className="flex fixed-height">
        <div className='flex flex-col w-full'>
          <div>
            <h1 className="text-lg font-bold">Edit Receipt Layout</h1>
            <h1 className="text-sm text-gray-400">Edit your receipt layout</h1>
            <div className='shrink-0 bg-border h-025 w-full my-5'></div>
            <div className='flex flex-col justify-between'>
              {/* Upload Image Field */}
                <div className='flex flex-col mb-2'>
                  <p className='text-sm font-semibold'>Image Logo</p>
                  <div className='flex gap-2 mt-2'>
                    <label htmlFor="ProductImage" data-content="ProductImage" 
                      className={`${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-500'} rounded px-3 py-2 text-sm text-center text-white shadow-md hover:bg-indigo-400 cursor-pointer`}>
                      Select Image
                    </label>
                  </div>
                  <input
                    id="ProductImage"
                    className='hidden'
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={handleFileInput}
                    disabled={isLoading}
                  />
                  <p className='text-xs mt-1 text-red-600'>{errorFile}</p>
                  {selectedFile && (
                    <p className='text-xs mt-1 text-blue-600'>
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
                <Input
                  disabled={isLoading}
                  label="Welcome"
                  placeholder="Header"
                  name="welcome"
                  value={data?.welcome || ''}
                  maxLength={100}
                  onChange={handleInputChange}
                />
                <TextArea
                  disabled={isLoading}
                  label="Store Name"
                  placeholder="Store Name"
                  name="customerService"
                  value={data?.customerService || ''}
                  maxLength={100}
                  onChange={handleTextAreaChange}
                />
                <Input
                  disabled={isLoading}
                  label="GST Number"
                  placeholder="123-123-123"
                  name="gst"
                  value={data?.gst || ''}
                  maxLength={100}
                  onChange={handleInputChange}
                />
                <Input
                  disabled={isLoading}
                  label="Transaction Message"
                  placeholder='Paid in full - Thank you'
                  name="paidInFull"
                  value={data?.paidInFull || ''}
                  maxLength={100}
                  onChange={handleInputChange}
                />
                <TextArea 
                  disabled={isLoading}
                  label="Footer"
                  placeholder='Thank you'
                  name="thankYou"
                  value={data?.thankYou || ''}
                  maxLength={200}
                  onChange={handleTextAreaChange}
                />
           </div>
            <Button
              isLoading={isLoading}
              className={`w-40 my-4 z-20 min-h-4 relative ${isLoading && 'cursor-default'}`}
              onClick={handleSubmit}
              disabled={isLoading}
            >
            {isLoading ? <SpinnerIcon /> : 'Submit'}
          </Button>
            </div>
        </div>
        <div className=''>
        <ReceiptPreview 
          isLoading={isLoading}
          imagePreview={getDisplayImage()}
          headingValue={data?.welcome || ''}
          storeName={data?.customerService || ''}
          transactionMsgValue={data?.paidInFull || ''}
          flavourTextValue={data?.thankYou || ''}
          gst={data?.gst || ''}
        />
        </div>
      </div>
      }
    </div>
    )
}

export default ReceiptLayoutPage